import { useEffect, useState } from "react";
import QuestionBox from "../../ui/QuestionBox/QuestionBox";
import classes from "../Infrastructure/Infrastructure.module.css";
import CommonFooter from "../../CommonFooter/CommonFooter";
import axios from "axios";
import { useAuth } from "../../../context/AuthContext";
import { env } from "../../../constants/env";
import { toast } from "react-toastify";
import InputField from "../../ui/InputField/InputField";
import { useParams } from "react-router-dom";
import CommonButton from "../../ui/CommonButton/CommonButton";
import styles from "./WeeklyReport.module.css";
import { useQuestionnaire } from "../../../hooks/useQuestionnaire";

const healthCenterTests = [
  "HIV bioline",
  "Oral quick",
  "Standard Q",
  "MTB cartridges",
  "TB LAM",
  "CD4 visitect",
  "CD4 presto",
  "Rapid Syphilis single",
  "Standard Q Syphilis/HIV",
  "Hepatitis B",
  "Cryp Antigen",
  "EDTA",
  "Red Top containers",
  "HIV Pima",
  "EID cartridges",
  "VI cartridges",
  "Malaria RDT",
  "DBS kits",
];

const initialState = {
  testsReferredToOtherLabs: [], // will override below
  backlog: {},
  backlogReason: {},
  kitsRemaining: {},
  stockMonths: {},
  availableInstrument: [],
  equipmentStatus: {},
};

const WeeklyReport = () => {
  const [currentSet, setCurrentSet] = useState(0);
  const [ownerLabId, setOwnerLabId] = useState(null);
  const [lastSubmittedAt, setLastSubmittedAt] = useState(null);
  const [isfetching, setIsFetching] = useState(false);
  const [newTestName, setNewTestName] = useState("");

  const { user } = useAuth();
  const { labId: paramLabId } = useParams();
  const labId = paramLabId || user?.labId;
  const userRole = user?.role;

  const {
    formData,
    setFormData,
    editMode,
    setEditMode,
    handleDone,
    fetchQuestionnaire,
    isFetching,
    loading,
    isOffline,
  } = useQuestionnaire("weekly-report", {
    ...initialState,
    testsReferredToOtherLabs:
      userRole === "HEALTH CENTER" ? healthCenterTests : ["", ""],
    availableInstrument: userRole === "HEALTH CENTER" ? [""] : {},
  });

  const isReadOnly =
    !["LABORATORY", "HEALTH CENTER"].includes(userRole) || !editMode;

  // --- update form state ---
  const handleChange = (field, value) => {
    if (isReadOnly) return;
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handlePerTestChange = (field, testName, value) => {
    if (isReadOnly) return;
    setFormData((prev) => ({
      ...prev,
      [field]: {
        ...prev[field],
        [testName]: value,
      },
    }));
  };

  // --- nav ---
  const handleNext = () => {
    if (currentSet < questionSets.length - 1) setCurrentSet(currentSet + 1);
  };

  const handlePrevious = () => {
    if (currentSet > 0) setCurrentSet(currentSet - 1);
  };

  // --- create a new weekly report ---
  const handleNewReport = () => {
    setFormData({
      ...initialState,
      testsReferredToOtherLabs:
        userRole === "HEALTH CENTER" ? healthCenterTests : ["", ""],
    });
    setEditMode(true);
  };

  // --- check if submitted this week ---
  const alreadySubmittedThisWeek = () => {
    if (!lastSubmittedAt) return false;
    const submitted = new Date(lastSubmittedAt);
    const now = new Date();
    const startOfWeek = new Date(now);
    startOfWeek.setDate(now.getDate() - now.getDay()); // Sunday start of week
    return submitted >= startOfWeek;
  };

  const questionSets =
    userRole === "HEALTH CENTER"
      ? [
          {
            subtitle: "",
            content: (
              <div className="d-flex flex-column gap-4">
                {/* Q5 ONLY */}
                <QuestionBox
                  number={1}
                  question="Month of stock for the specified available test kits"
                  disabled={isReadOnly}
                >
                  <div className="d-flex flex-column gap-2">
                    {[
                      ...healthCenterTests,
                      ...(formData.testsReferredToOtherLabs || []),
                    ]
                      .filter(
                        (t, index, self) =>
                          t.trim() !== "" && self.indexOf(t) === index
                      )
                      .map((test, idx) => (
                        <div
                          key={idx}
                          className={`${classes.customInput} d-flex align-items-center gap-2`}
                        >
                          <div className={classes.prepend}>{test}</div>
                          <input
                            type="text"
                            placeholder="Enter months"
                            value={formData.stockMonths?.[test] || ""}
                            onChange={(e) =>
                              handlePerTestChange(
                                "stockMonths",
                                test,
                                e.target.value
                              )
                            }
                            disabled={isReadOnly}
                          />
                        </div>
                      ))}

                    {/* ➕ Add new test row */}
                    {!isReadOnly && (
                      <div
                        className={`${classes.customInput} d-flex align-items-center gap-2`}
                      >
                        <input
                          type="text"
                          placeholder="Enter new test name"
                          value={newTestName}
                          onChange={(e) => setNewTestName(e.target.value)}
                        />
                        <button
                          type="button"
                          onClick={() => {
                            if (newTestName.trim() !== "") {
                              setFormData((prev) => ({
                                ...prev,
                                testsReferredToOtherLabs: [
                                  ...prev.testsReferredToOtherLabs,
                                  newTestName.trim(),
                                ],
                              }));
                              setNewTestName("");
                            }
                          }}
                          className={styles.addButton}
                        >
                          Add
                        </button>
                      </div>
                    )}
                  </div>
                </QuestionBox>

                <QuestionBox
                  number={2}
                  question="Available instrument"
                  singleInput
                  answers={formData.availableInstrument}
                  onAnswersChange={(newAnswers) =>
                    handleChange("availableInstrument", newAnswers)
                  }
                  disabled={isReadOnly}
                />

                {/* Q7 */}
                <QuestionBox
                  number={3}
                  question="What is the equipment status"
                  disabled={isReadOnly}
                >
                  <div className="d-flex flex-column gap-2">
                    {(formData.availableInstrument || [])
                      .filter((instrument) => instrument.trim() !== "")
                      .map((instrument, idx) => (
                        <div
                          key={idx}
                          className={`${classes.customInput} d-flex align-items-center gap-2`}
                        >
                          {/* prepend instrument name only */}
                          <div className={classes.prepend}>
                            <strong>{instrument}</strong>
                          </div>

                          <InputField
                            dropdownClass={styles.dropdownClass}
                            name={`equipmentStatus-${idx}`} // unique name per instrument
                            value={formData.equipmentStatus?.[instrument] || ""}
                            onChange={(e) =>
                              handlePerTestChange(
                                "equipmentStatus",
                                instrument,
                                e.target.value
                              )
                            }
                            placeholder="Select status"
                            disabled={isReadOnly}
                            dropdownOptions={[
                              { label: "Functional", value: "Functional" },
                              {
                                label: "Needs Maintenance",
                                value: "Needs Maintenance",
                              },
                              {
                                label: "Not applicable",
                                value: "Not applicable",
                              },
                            ]}
                          />
                        </div>
                      ))}
                  </div>
                </QuestionBox>
              </div>
            ),
          },
        ]
      : [
          {
            subtitle: "",
            content: (
              <div className="d-flex flex-column gap-4">
                <QuestionBox
                  number={1}
                  question="⁠Specify test name"
                  singleInput
                  answers={formData.testsReferredToOtherLabs}
                  onAnswersChange={(newAnswers) =>
                    handleChange("testsReferredToOtherLabs", newAnswers)
                  }
                  disabled={isReadOnly}
                />

                {/* Q2 */}
                <QuestionBox
                  number={2}
                  question="Number of samples pending for the specified test name (backlog)"
                  disabled={isReadOnly}
                >
                  <div className="d-flex flex-column gap-2">
                    {(formData.testsReferredToOtherLabs || [])
                      .filter((t) => t.trim() !== "")
                      .map((test, idx) => (
                        <div
                          key={idx}
                          className={`${classes.customInput} d-flex align-items-center gap-2`}
                        >
                          <div className={classes.prepend}>{test}</div>
                          <input
                            type="text"
                            placeholder="Enter number"
                            value={formData.backlog?.[test] || ""}
                            onChange={(e) =>
                              handlePerTestChange(
                                "backlog",
                                test,
                                e.target.value
                              )
                            }
                            disabled={isReadOnly}
                          />
                        </div>
                      ))}
                  </div>
                </QuestionBox>

                {/* Q3 */}
                <QuestionBox
                  number={3}
                  question="Reason for backlog"
                  disabled={isReadOnly}
                >
                  <div className="d-flex flex-column gap-2">
                    {(formData.testsReferredToOtherLabs || [])
                      .filter((t) => t.trim() !== "")
                      .map((test, idx) => (
                        <div
                          key={idx}
                          className={`${classes.customInput} d-flex align-items-center gap-2`}
                        >
                          <div className={classes.prepend}>{test}</div>
                          <input
                            type="text"
                            placeholder="Enter reason"
                            value={formData.backlogReason?.[test] || ""}
                            onChange={(e) =>
                              handlePerTestChange(
                                "backlogReason",
                                test,
                                e.target.value
                              )
                            }
                            disabled={isReadOnly}
                          />
                        </div>
                      ))}
                  </div>
                </QuestionBox>

                {/* Q4 */}
                <QuestionBox
                  number={4}
                  question="Test kits remaining for the specified test name"
                  disabled={isReadOnly}
                >
                  <div className="d-flex flex-column gap-2">
                    {(formData.testsReferredToOtherLabs || [])
                      .filter((t) => t.trim() !== "")
                      .map((test, idx) => (
                        <div
                          key={idx}
                          className={`${classes.customInput} d-flex align-items-center gap-2`}
                        >
                          <div className={classes.prepend}>{test}</div>
                          <input
                            type="text"
                            placeholder="Enter number"
                            value={formData.kitsRemaining?.[test] || ""}
                            onChange={(e) =>
                              handlePerTestChange(
                                "kitsRemaining",
                                test,
                                e.target.value
                              )
                            }
                            disabled={isReadOnly}
                          />
                        </div>
                      ))}
                  </div>
                </QuestionBox>

                {/* Q5 */}
                <QuestionBox
                  number={5}
                  question="Month of stock for the specified available test kits"
                  disabled={isReadOnly}
                >
                  <div className="d-flex flex-column gap-2">
                    {(formData.testsReferredToOtherLabs || [])
                      .filter((t) => t.trim() !== "")
                      .map((test, idx) => (
                        <div
                          key={idx}
                          className={`${classes.customInput} d-flex align-items-center gap-2`}
                        >
                          <div className={classes.prepend}>{test}</div>
                          <input
                            type="text"
                            placeholder="Enter months"
                            value={formData.stockMonths?.[test] || ""}
                            onChange={(e) =>
                              handlePerTestChange(
                                "stockMonths",
                                test,
                                e.target.value
                              )
                            }
                            disabled={isReadOnly}
                          />
                        </div>
                      ))}
                  </div>
                </QuestionBox>

                {/* Q6 */}
                <QuestionBox
                  number={6}
                  question="Available instrument"
                  disabled={isReadOnly}
                >
                  <div className="d-flex flex-column gap-2">
                    {(formData.testsReferredToOtherLabs || [])
                      .filter((t) => t.trim() !== "")
                      .map((test, idx) => (
                        <div
                          key={idx}
                          className={`${classes.customInput} d-flex align-items-center gap-2`}
                        >
                          <div className={classes.prepend}>{test}</div>
                          <input
                            type="text"
                            placeholder="Enter instrument name"
                            value={formData.availableInstrument?.[test] || ""}
                            onChange={(e) =>
                              handlePerTestChange(
                                "availableInstrument",
                                test,
                                e.target.value
                              )
                            }
                            disabled={isReadOnly}
                          />
                        </div>
                      ))}
                  </div>
                </QuestionBox>

                {/* Q7 */}
                <QuestionBox
                  number={7}
                  question="What is the equipment status"
                  disabled={isReadOnly}
                >
                  <div className="d-flex flex-column gap-2">
                    {Object.entries(formData.availableInstrument || {})
                      .filter(([test, instrument]) => instrument.trim() !== "")
                      .map(([test, instrument], idx) => (
                        <div
                          key={idx}
                          className={`${classes.customInput} d-flex align-items-center gap-2`}
                        >
                          <div className={classes.prepend}>
                            {test}: <strong>{instrument}</strong>
                          </div>
                          <InputField
                            dropdownClass={styles.dropdownClass}
                            name={`equipmentStatus-${test}`}
                            value={formData.equipmentStatus?.[test] || ""}
                            onChange={(e) =>
                              handlePerTestChange(
                                "equipmentStatus",
                                test,
                                e.target.value
                              )
                            }
                            placeholder="Select status"
                            disabled={isReadOnly}
                            dropdownOptions={[
                              { label: "Functional", value: "Functional" },
                              {
                                label: "Needs Maintenance",
                                value: "Needs Maintenance",
                              },
                              {
                                label: "Not applicable",
                                value: "Not applicable",
                              },
                            ]}
                          />
                        </div>
                      ))}
                  </div>
                </QuestionBox>
              </div>
            ),
          },
        ];

  const { subtitle, content } = questionSets[currentSet];

  if (isFetching) {
    return <div className={classes.loading}>Loading...</div>;
  }

  return (
    <div
      className={`${classes.wrapper} ${paramLabId ? classes.fromParams : ""} ${
        isReadOnly ? classes.readOnly : ""
      }`}
    >
      <div className="d-flex justify-content-between align-items-center">
        <h2>Weekly Testing Report</h2>
        {["LABORATORY", "HEALTH CENTER"].includes(userRole) && !editMode && (
          <div className="d-flex align-items-center gap-3">
            <CommonButton
              className={classes.editButton}
              onClick={handleNewReport}
              title="New Report"
            />

            <CommonButton
              className={classes.editButton}
              onClick={() => setEditMode(true)}
              title="Edit"
            />
          </div>
        )}
      </div>

      <h4>{subtitle}</h4>
      {!editMode && !["MINISTRY"].includes(userRole) ? (
        <div className={classes.infoText}>
          ✅ Your weekly report has been submitted for this week
        </div>
      ) : (
        content
      )}

      {!isReadOnly && (
        <CommonFooter
          handlePrevious={handlePrevious}
          handleNext={handleNext}
          handleDone={handleDone}
          doneText={
            loading ? "Submitting..." : isOffline ? "Save Offline" : "Submit"
          }
          cancelText="Cancel"
          handleCancel={() => {
            setEditMode(false);
            fetchQuestionnaire();
            setCurrentSet(0);
          }}
          questionSets={questionSets}
          currentSet={currentSet}
          disabled={loading}
        />
      )}
    </div>
  );
};

export default WeeklyReport;
